// Time-on-move capture (handoff 7.5). When a piece moves stages (board
// drag or This Week check), this small prompt asks how long the finished
// stage took and logs it to marketing_time_log so CAC stays honest.
// The activity logged is the stage the item just LEFT (that's the work
// that was completed), keyed to the content piece. A successful log
// refetches the marketing summary so the CAC tile, Budget screen, and
// the Performance scorecard's Invested column update without a reload.

import { useState } from 'react';
import { apiFetch } from '../../../utils/apiFetch';
import { STAGE_LABELS } from '../stages';
import { useMarketing } from '../MarketingContext';

const CHIPS = [0.5, 1.5, 3];

export default function TimePromptModal({ prompt, onClose }) {
  // prompt = { contentId, title, fromStage, toStage } | null
  const { reloadSummary } = useMarketing();
  const [custom, setCustom] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  if (!prompt) return null;

  async function log(hours) {
    setErr(null);
    setSaving(true);
    try {
      const res = await apiFetch('/api/data/marketing/time-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          activityType: prompt.fromStage,
          hours,
          contentLogId: prompt.contentId,
          notes: `${STAGE_LABELS[prompt.fromStage] || prompt.fromStage} -> ${STAGE_LABELS[prompt.toStage] || prompt.toStage}`,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      reloadSummary();
      onClose();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  function handleCustom() {
    const h = Number(custom);
    if (!Number.isFinite(h) || h <= 0) { setErr('Enter hours > 0'); return; }
    log(h);
  }

  return (
    <div className="fixed inset-0 z-50 bg-navy/20 grid place-items-center" onClick={onClose}>
      <div
        className="bg-white border border-deep-teal rounded-[10px] shadow-[0_8px_24px_rgba(2,10,40,0.10)] p-4 w-[360px]"
        onClick={e => e.stopPropagation()}
      >
        <div className="font-black text-navy text-[13px]">
          Moved "{prompt.title}" to {STAGE_LABELS[prompt.toStage] || prompt.toStage}
        </div>
        <div className="text-ink-2 text-[12px] mt-1">
          Log time on {STAGE_LABELS[prompt.fromStage]?.toLowerCase() || prompt.fromStage} so CAC stays accurate.
        </div>
        <div className="flex gap-2 mt-2.5 flex-wrap items-center">
          {CHIPS.map(h => (
            <button
              key={h}
              disabled={saving}
              onClick={() => log(h)}
              className="border border-line rounded-[7px] px-3 py-1.5 text-[12px] font-bold text-ink-2 hover:bg-deep-teal hover:text-white hover:border-deep-teal transition-colors"
            >
              {h}h
            </button>
          ))}
          <input
            value={custom}
            onChange={e => setCustom(e.target.value)}
            placeholder="Custom"
            className="w-[64px] border border-line rounded-[7px] px-2 py-1.5 text-[12px] focus:outline-none focus:border-bullogic-blue"
            onKeyDown={e => { if (e.key === 'Enter') handleCustom(); }}
          />
          {custom && (
            <button
              disabled={saving}
              onClick={handleCustom}
              className="border border-deep-teal bg-deep-teal text-white rounded-[7px] px-3 py-1.5 text-[12px] font-bold"
            >
              Log
            </button>
          )}
          <button
            disabled={saving}
            onClick={onClose}
            className="border border-dashed border-line rounded-[7px] px-3 py-1.5 text-[12px] font-bold text-ink-3 hover:text-ink"
          >
            Skip
          </button>
        </div>
        {err && <div className="text-[11px] text-rose mt-2">{err}</div>}
      </div>
    </div>
  );
}
